import type WorkSummaryDetailVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

const BillingTypeLabel = {
	[WorkSummaryBillingType.BILLABLE_WORK]: 'Work',
	[WorkSummaryBillingType.MATERIAL_COST]: 'Material Expense',
};

enum WORK_SUMMARY_DETAILS_TABLE_HEADERS {
	BILLABLE = 'Billable',
	DATE = 'Date',
	WORK_ORDER = 'Work Order',
	FR_SEGMENT = 'FR Segment',
	FR_SECONDARY_SEGMENT = 'FR Secondary Segment',
	WORK = 'Work',
	TYPE = 'Type',
	DEFINITION_FIELD = 'Definition',
	QUANITITY = 'Quanitity',
	UNIT = 'Unit',
	INFORMATIONAL_FIELD = 'Info',
	BILLING_CODE_ID = 'Billing code ID',
	BILLING_CODE_DESCRIPTION = 'Billing code description'
}

export const toCSVData = (workSummaryDetails: WorkSummaryDetailVM[], timeZone: Nullable<string>): string[][] => {
	const headers = resolveWorkSummaryDetailsTableHeaders(workSummaryDetails);
	const maxNumberOfDefinitionFields = workSummaryDetails.reduce(maxDefinitionFieldsReducer, 0);
	const maxNumberOfInformationalFields = workSummaryDetails.reduce(maxInformationalFieldsReducer, 0);
	const rows: string[][] = workSummaryDetails.map((_wsd) => {
		const definitionFieldsValues: string[] = [];
		const informationalFieldsValues: string[] = [];
		for (let i = 0; i < maxNumberOfDefinitionFields; i++) {
			const defName = _wsd.definitionFields[i]?.name ? `${_wsd.definitionFields[i]?.name}: ` : '';
			const defValue = _wsd.definitionFields[i] ? _resolveFieldValueForExport(_wsd.definitionFields[i].value, _wsd.definitionFields[i].fieldType, timeZone) : 'N/A'; const defUnit = _wsd.definitionFields[i]?.fieldUnit ? ` ${_wsd.definitionFields[i]?.fieldUnit}` : '';
			definitionFieldsValues.push(`${defName}${defValue}${defUnit}`);
		}

		for (let i = 0; i < maxNumberOfInformationalFields; i++) {
			const infName = _wsd.informationalFields[i]?.name ? `${_wsd.informationalFields[i]?.name}: ` : '';
			const infValue = _wsd.informationalFields[i] ? _resolveFieldValueForExport(_wsd.informationalFields[i].value, _wsd.informationalFields[i].fieldType, timeZone) : 'N/A'; const infUnit = _wsd.informationalFields[i]?.fieldUnit ? ` ${_wsd.informationalFields[i]?.fieldUnit}` : '';
			informationalFieldsValues.push(`${infName}${infValue}${infUnit}`);
		}

		const workTypeAdjustedName = (
			_wsd.workTypeFieldValueType === ReportBlockFieldEnum.ValueType.NUMBER
			|| _wsd.workTypeFieldValueType === ReportBlockFieldEnum.ValueType.CALCULATED
		)
			? _wsd.workTypeFieldName
			: `${_wsd.workTypeFieldName}: ${_resolveFieldValueForExport(_wsd.workType, _wsd.workTypeFieldType, timeZone)}`;

		return [
			BillingTypeLabel[_wsd.billingType],
			_wsd.isBillable ? 'Yes' : 'No',
			_wsd.date,
			_wsd.workOrder,
			_wsd.frSegment ?? 'N/A',
			_wsd.frSegmentSecondary ?? 'N/A',
			_wsd.workName ?? 'N/A',
			_wsd.workType ? workTypeAdjustedName : 'N/A',
			...definitionFieldsValues,
			_wsd.quantity + '',
			_wsd.unit ?? 'N/A',
			...informationalFieldsValues,
			_wsd.billingCodeCustomerId ?? 'N/A',
			_wsd.billingCodeDescription ?? 'N/A',
		];
	});

	return [headers, ...rows];
};

const resolveWorkSummaryDetailsTableHeaders = (workSummaryDetails: WorkSummaryDetailVM[]) => {
	const maxNumberOfDefinitionFields = workSummaryDetails.reduce(maxDefinitionFieldsReducer, 0);
	const maxNumberOfInformationalFields = workSummaryDetails.reduce(maxInformationalFieldsReducer, 0);

	const headers: string[] = ['Billing Type'];
	const headerTemplateKeys = Object.keys(WORK_SUMMARY_DETAILS_TABLE_HEADERS);
	for (const headerTemplateKey of headerTemplateKeys) {
		if (WORK_SUMMARY_DETAILS_TABLE_HEADERS[headerTemplateKey] === WORK_SUMMARY_DETAILS_TABLE_HEADERS.DEFINITION_FIELD) {
			for (let i = 0; i < maxNumberOfDefinitionFields; i++) {
				headers.push(`${WORK_SUMMARY_DETAILS_TABLE_HEADERS[headerTemplateKey]} ${i + 1}`);
			}
		}
		else if (WORK_SUMMARY_DETAILS_TABLE_HEADERS[headerTemplateKey] === WORK_SUMMARY_DETAILS_TABLE_HEADERS.INFORMATIONAL_FIELD) {
			for (let i = 0; i < maxNumberOfInformationalFields; i++) {
				headers.push(`${WORK_SUMMARY_DETAILS_TABLE_HEADERS[headerTemplateKey]} ${i + 1}`);
			}
		}
		else {
			headers.push(WORK_SUMMARY_DETAILS_TABLE_HEADERS[headerTemplateKey]);
		}
	}

	return headers;
};

const maxDefinitionFieldsReducer = (_max: number, _currentWSD: WorkSummaryDetailVM) => {
	return _currentWSD.definitionFields.length > _max ? _currentWSD.definitionFields.length : _max;
};

const maxInformationalFieldsReducer = (_max: number, _currentWSD: WorkSummaryDetailVM) => {
	return _currentWSD.informationalFields.length > _max ? _currentWSD.informationalFields.length : _max;
};

function _resolveFieldValueForExport(value: Nullable<string>, type: ReportBlockFieldEnum.Type, timeZone: Nullable<string>) {
	if (!value) {
		return 'N/A';
	}

	let resolvedValue = value;

	if (type === ReportBlockFieldEnum.Type.BOOLEAN) {
		resolvedValue = new Boolean(value) ? 'Yes' : 'No';
	}

	if (type === ReportBlockFieldEnum.Type.ADDRESS) {
		// since non repeatable address fields are saved as stringified objects
		const isNonRepeatingField = (typeof value === 'string');

		return isNonRepeatingField
			? JSON.parse(value).street
			: (value as unknown as { street?: string; })?.street;
	}

	if (type === ReportBlockFieldEnum.Type.EQUIPMENT) {
		const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
		return `${parsedValue.code ?? ''} ${parsedValue.specification ? `(${parsedValue.specification})` : ''}`;
	}
	if (type === ReportBlockFieldEnum.Type.SIGNATURE) {
		const isNonRepeatingField = (typeof value === 'string');

		const signatures: string[] = isNonRepeatingField
			? [value]
			: value;

		return signatures.map((_signature) => {
			const signatureParsed = JSON.parse(_signature);

			if (signatureParsed.name && signatureParsed.imageUrl) {
				const timeStamp = `${TimeUtils.formatDate(signatureParsed.signedAt, TimeFormat.FULL_DATE, TimeFormat.ISO_DATETIME, timeZone ?? undefined)}, ${TimeUtils.abbreviateTimeZone(timeZone, 'z (Z)')}`;
				return `${signatureParsed.name} ${timeStamp}`;
			}
		}).join(',');
	}

	return resolvedValue;
}
